// The label editor: a paper label on a canvas, edited with Pointer Events
// (mouse, touch, pen), plus the useLabelDocument() hook that owns the
// document, selection, style defaults and undo history.
//
// Patterns (from the dsd-hub AnnotationCanvas, adapted):
// - The document is vector (mm) and replayed on every draw; nothing is baked.
// - Two coordinate systems: CSS px on screen vs label mm. The stage gets a CSS
//   `translate + scale` for zoom/pan (never rotate), so getBoundingClientRect()
//   of the stage IS the on-screen box and pointer → mm is one division.
// - All pointer listeners live on the outer box, not the canvas, so a pinch's
//   second finger landing outside the label still counts.
// - Snapshot undo: the pre-change doc is recorded before every mutating
//   gesture; a drag/resize/typing session shares one history key.
// - The <textarea> editor is ALWAYS mounted (invisible when idle) so it can be
//   focused synchronously inside the tap handler — that is what makes iOS
//   raise the keyboard.
// - Canvas v2 (W5): ornaments are drawn by drawLabel like any element and the
//   canvas redraws as each finishes decoding; templates arrive through
//   applyTemplate and finished artwork through placeArtwork. A painted
//   artwork (M5, "Describe your design") arrives through placeArtworkWithText,
//   which also seeds the wording as editable text.
// SSR: no browser API at module scope or during render; everything that
// touches window/document/canvas runs in effects or event handlers.
import * as React from "react";
import { cn } from "@/lib/utils";
import type { Assets } from "@/lib/label-canvas/bottles";
import {
  DEFAULT_STYLE,
  applyStyle,
  createFrame,
  createOrnament,
  createText,
  describeElement,
  placeArtwork as placeArtworkInDoc,
  placeImage as placeImageInDoc,
  rebottle,
  sanitiseStyle,
  styleOf,
  type FrameKind,
  type StyleDefaults,
  type StylePatch,
} from "@/lib/label-canvas/elements";
import { cssFamily, loadFonts, resolveFace } from "@/lib/label-canvas/fonts";
import {
  handleCursor,
  handlePoints,
  hitHandle,
  hitTest,
  moveElement,
  nudge,
  resizeElement,
  type Handle,
  type Point,
} from "@/lib/label-canvas/geometry";
import * as H from "@/lib/label-canvas/history";
import { subscribeOrnaments, type OrnamentKey } from "@/lib/label-canvas/ornaments";
import { PALETTE_HEX } from "@/lib/label-canvas/palette";
import { drawLabel, faceMetrics, measureTextHeightMm } from "@/lib/label-canvas/render";
import {
  LIMITS,
  findElement,
  migrate,
  removeElement,
  replaceElement,
  type BottleType,
  type CanvasData,
  type ImageElement,
  type LabelElement,
  type OrnamentElement,
  type TextElement,
} from "@/lib/label-canvas/schema";
import { centred } from "@/lib/label-canvas/templates/kit";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CanvasTool = "select" | "text";

/**
 * How a change enters the undo history.
 * - "push" (default): record the pre-change doc — unless `key` equals the
 *   previous record's key (same gesture), then nothing new is recorded.
 * - "skip": no history (e.g. re-measuring text after fonts load).
 * - "drop": the gesture `key` cancelled itself; remove its record.
 */
export interface ChangeMeta {
  history?: "push" | "skip" | "drop";
  key?: string;
}

export interface LabelCanvasHandle {
  /** Open the in-place text editor for a text element (call inside the click handler so iOS raises the keyboard). */
  startEditing(id: string): void;
  /** Close the text editor, committing its text. */
  stopEditing(): void;
}

export interface LabelCanvasProps {
  doc: CanvasData;
  onChange: (doc: CanvasData, meta?: ChangeMeta) => void;
  assets: Assets;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** "text": the next tap on the label places a new text element there. */
  tool: CanvasTool;
  /** Called after the text tool has placed an element (the parent usually returns to "select"). */
  onToolDone?: () => void;
  /** 1 = label fitted to the available box. Clamp with clampZoom(). */
  zoom: number;
  onZoom: (zoom: number) => void;
  readOnly?: boolean;
  /** True while the overlaid text editor is open (e.g. hide the mobile toolbar). */
  onEditingChange?: (editing: boolean) => void;
  /** Style for text placed with the text tool — pass the hook's `defaults`. */
  defaults?: StyleDefaults;
  className?: string;
  /** Accessible description of the label canvas (a summary is generated otherwise). */
  ariaLabel?: string;
  ref?: React.Ref<LabelCanvasHandle>;
}

export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 1.25;
export function clampZoom(z: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number.isFinite(z) ? z : 1));
}

// ---------------------------------------------------------------------------
// useLabelDocument — document + selection + defaults + undo in one place
// ---------------------------------------------------------------------------

function withTextHeight(el: LabelElement): LabelElement {
  if (el.type !== "text") return el;
  const height = measureTextHeightMm(el);
  return Math.abs(height - el.height) < 0.005 ? el : { ...el, height };
}

/** Wording seeded over a painted artwork (M5 decision 10): what, and where. */
export interface SeededWording {
  /** "Name on the label"; blank means no name element. */
  name?: string;
  /** "Occasion line"; blank means no line element. */
  line?: string;
}

/**
 * Seeded wording geometry (M5 decision 10): Cormorant Garamond in gold,
 * centred across the label with the 6 mm default margin, positioned by the
 * first line's baseline as a fraction of the label height. A long entry is
 * stepped down 0.25 mm at a time, never below `floor`, so it stays on one
 * line rather than wrapping into the line below. Measured in Cormorant: at
 * the floors the sheet's longest entries (32 and 48 characters) fit the MCC
 * label's 68 mm column; "Salt House" keeps its full 6 mm.
 */
const SEEDED = {
  name: { size: 6, floor: 2.5, baseline: 0.62, tracking: 0.06 },
  line: { size: 3, floor: 1.8, baseline: 0.72, tracking: 0.08 },
} as const;

/** True when a text element's wording wraps to more than one line. */
function wraps(el: TextElement): boolean {
  return measureTextHeightMm(el) > measureTextHeightMm({ ...el, text: "H" }) * 1.01;
}

function seededText(doc: CanvasData, kind: keyof typeof SEEDED, raw: string | undefined): TextElement | null {
  const text = (raw ?? "").replace(/\s+/g, " ").trim().slice(0, LIMITS.maxTextLength);
  if (!text) return null;
  const spec = SEEDED[kind];
  const build = (size: number) =>
    centred(doc.bottle, {
      text,
      font: "cormorant",
      size,
      color: PALETTE_HEX.gold,
      baseline: Math.round(doc.label.height_mm * spec.baseline * 100) / 100,
      tracking: spec.tracking,
    });
  let size: number = spec.size;
  let el = build(size);
  while (size > spec.floor && wraps(el)) {
    size = Math.max(spec.floor, Math.round((size - 0.25) * 100) / 100);
    el = build(size);
  }
  return withTextHeight(el) as TextElement;
}

export interface LabelDocument {
  doc: CanvasData;
  /** Replace the document (history per `meta`, default push). */
  set: (next: CanvasData, meta?: ChangeMeta) => void;
  /** Patch one element; text height is re-measured automatically. */
  update: (id: string, patch: Partial<LabelElement>, meta?: ChangeMeta) => void;
  /** Append an element (top of the stack) and select it. */
  add: (el: LabelElement, opts?: { select?: boolean }) => void;
  remove: (id: string) => void;
  undo: () => void;
  canUndo: boolean;
  /** Remove every element (background and bottle stay); undoable. */
  clearAll: () => void;
  /** Load a document afresh (server load, "Start again"): clears history and selection. */
  reset: (next: CanvasData | unknown, fallbackBottle?: BottleType) => void;

  selectedId: string | null;
  select: (id: string | null) => void;
  selected: LabelElement | null;

  /** Style for the NEXT element (used when nothing is selected). */
  defaults: StyleDefaults;
  setDefaults: (patch: StylePatch) => void;
  /** What the inspector should show: the selected element's style, else the defaults. */
  style: StyleDefaults;
  /** Retroactive restyle: applies to the selected element, or sets defaults when none is selected. */
  restyle: (patch: StylePatch) => void;

  setBackground: (hex: string) => void;
  /** Switch wine ↔ MCC; the design is scaled uniformly to the new label. */
  setBottle: (bottle: BottleType) => void;
  /** Add a text element centred on the label using the defaults; returns it (selected). */
  addText: (text?: string) => TextElement;
  /** Add a rectangle border, oval frame or divider using the defaults; returns it (selected). */
  addFrame: (kind: FrameKind) => LabelElement;
  /** Place/replace the single uploaded image from its natural pixel size; returns it (selected). */
  placeImage: (natural: { width: number; height: number }) => ImageElement;

  /**
   * Replace the whole design with a template document (`template.build(bottle)`):
   * one undo entry (Undo brings the previous design back), selection cleared,
   * style defaults untouched. The CURRENT bottle is kept — a document built
   * for the other bottle is re-bottled. Text heights are re-measured.
   */
  applyTemplate: (next: CanvasData) => void;
  /** Add an ornament in `ornamentColor`, placed by createOrnament(); returns it (selected). */
  addOrnament: (key: OrnamentKey) => OrnamentElement;
  /**
   * "I have my own artwork": clear every element and place the single image
   * fitted to the whole label (0 mm margin, contain). One undo entry; the
   * image is selected and returned.
   */
  placeArtwork: (natural: { width: number; height: number }) => ImageElement;
  /**
   * "Describe your design" → Use this (M5): placeArtwork() plus up to two
   * centred text elements (name at 62 % of the label height, occasion line at
   * 72 %), all as ONE undo entry, with the selection cleared. Call once the
   * label fonts have loaded, so a long entry's one-line fit is measured with
   * Cormorant itself. Returns the image element.
   */
  placeArtworkWithText: (natural: { width: number; height: number }, texts: SeededWording) => ImageElement;
  /** True when the label has no elements (a template applies without a confirm). */
  isEmpty: boolean;
  /** Colour a new ornament gets: the decoration default (`defaults.stroke`, gold unless changed). */
  ornamentColor: string;

  /** Spread onto <LabelCanvas>: doc, onChange, selectedId, onSelect, defaults. */
  canvasProps: Pick<LabelCanvasProps, "doc" | "onChange" | "selectedId" | "onSelect" | "defaults">;
}

interface DocState {
  doc: CanvasData;
  history: H.History<CanvasData>;
}

/**
 * `initial` may be raw `canvas_data` from the server (it is migrate()d);
 * `fallbackBottle` is the mock-up's bottle_type / the ?bottle prefill.
 */
export function useLabelDocument(
  initial: CanvasData | unknown,
  fallbackBottle: BottleType = "wine",
): LabelDocument {
  const [state, setState] = React.useState<DocState>(() => ({
    doc: migrate(initial, fallbackBottle),
    history: H.emptyHistory<CanvasData>(),
  }));
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [defaults, setDefaultsState] = React.useState<StyleDefaults>(DEFAULT_STYLE);

  // Latest state for callbacks that must not re-create on every change.
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const selectedRef = React.useRef(selectedId);
  selectedRef.current = selectedId;

  const commit = React.useCallback((next: CanvasData, meta?: ChangeMeta) => {
    const cur = stateRef.current;
    if (next === cur.doc) return;
    const mode = meta?.history ?? "push";
    let history = cur.history;
    if (mode === "push") history = H.record(history, cur.doc, meta?.key);
    else if (mode === "drop" && meta?.key) history = H.drop(history, meta.key);
    const s = { doc: next, history };
    stateRef.current = s;
    setState(s);
  }, []);

  const set = React.useCallback(
    (next: CanvasData, meta?: ChangeMeta) => {
      commit(next, meta);
      // Selection must not point at an element that no longer exists.
      const sel = selectedRef.current;
      if (sel && !next.elements.some((e) => e.id === sel)) setSelectedId(null);
    },
    [commit],
  );

  const update = React.useCallback(
    (id: string, patch: Partial<LabelElement>, meta?: ChangeMeta) => {
      const doc = stateRef.current.doc;
      const el = findElement(doc, id);
      if (!el) return;
      const nextEl = withTextHeight({ ...el, ...patch, id: el.id, type: el.type } as LabelElement);
      commit({ ...doc, elements: doc.elements.map((e) => (e.id === id ? nextEl : e)) }, meta);
    },
    [commit],
  );

  const add = React.useCallback(
    (el: LabelElement, opts?: { select?: boolean }) => {
      const doc = stateRef.current.doc;
      // Keyed so that typing into a new text element (LabelCanvas uses the
      // same `create-<id>` key) folds into this one undo entry, and closing it
      // empty removes the entry again.
      commit({ ...doc, elements: [...doc.elements, withTextHeight(el)] }, { key: `create-${el.id}` });
      if (opts?.select !== false) setSelectedId(el.id);
    },
    [commit],
  );

  const remove = React.useCallback(
    (id: string) => {
      const doc = stateRef.current.doc;
      if (!doc.elements.some((e) => e.id === id)) return;
      commit({ ...doc, elements: doc.elements.filter((e) => e.id !== id) });
      if (selectedRef.current === id) setSelectedId(null);
    },
    [commit],
  );

  const undo = React.useCallback(() => {
    const cur = stateRef.current;
    const r = H.undo(cur.history);
    if (!r) return;
    const s = { doc: r.value, history: r.history };
    stateRef.current = s;
    setState(s);
    const sel = selectedRef.current;
    if (sel && !r.value.elements.some((e) => e.id === sel)) setSelectedId(null);
  }, []);

  const clearAll = React.useCallback(() => {
    const doc = stateRef.current.doc;
    if (doc.elements.length === 0) return;
    commit({ ...doc, elements: [] });
    setSelectedId(null);
  }, [commit]);

  const reset = React.useCallback(
    (next: CanvasData | unknown, fb: BottleType = fallbackBottle) => {
      const s = { doc: migrate(next, fb), history: H.emptyHistory<CanvasData>() };
      stateRef.current = s;
      setState(s);
      setSelectedId(null);
    },
    [fallbackBottle],
  );

  const setDefaults = React.useCallback((patch: StylePatch) => {
    setDefaultsState((d) => ({ ...d, ...sanitiseStyle(patch) }));
  }, []);

  const restyle = React.useCallback(
    (patch: StylePatch) => {
      const doc = stateRef.current.doc;
      const el = findElement(doc, selectedRef.current);
      if (!el) {
        setDefaults(patch);
        return;
      }
      const nextEl = withTextHeight(applyStyle(el, patch));
      commit({ ...doc, elements: doc.elements.map((e) => (e.id === el.id ? nextEl : e)) });
    },
    [commit, setDefaults],
  );

  const setBackground = React.useCallback(
    (hex: string) => {
      const doc = stateRef.current.doc;
      if (doc.background.color === hex) return;
      commit({ ...doc, background: { color: hex } });
    },
    [commit],
  );

  const setBottle = React.useCallback(
    (bottle: BottleType) => {
      const doc = stateRef.current.doc;
      if (doc.bottle === bottle) return;
      const next = rebottle(doc, bottle);
      commit({ ...next, elements: next.elements.map(withTextHeight) });
    },
    [commit],
  );

  const addText = React.useCallback(
    (text = "") => {
      const el = createText(stateRef.current.doc, null, defaults, text) as TextElement;
      add(el);
      return withTextHeight(el) as TextElement;
    },
    [add, defaults],
  );

  const addFrame = React.useCallback(
    (kind: FrameKind) => {
      const el = createFrame(stateRef.current.doc, kind, defaults);
      add(el);
      return el;
    },
    [add, defaults],
  );

  const placeImage = React.useCallback(
    (natural: { width: number; height: number }) => {
      const r = placeImageInDoc(stateRef.current.doc, natural);
      commit(r.doc);
      setSelectedId(r.element.id);
      return r.element;
    },
    [commit],
  );

  const applyTemplate = React.useCallback(
    (tpl: CanvasData) => {
      const cur = stateRef.current.doc;
      // migrate() re-validates (and copies) the template; a bad element is dropped, never drawn.
      let next = migrate(tpl, cur.bottle);
      if (next.bottle !== cur.bottle) next = rebottle(next, cur.bottle);
      commit({ ...next, elements: next.elements.map(withTextHeight) });
      setSelectedId(null);
    },
    [commit],
  );

  const addOrnament = React.useCallback(
    (key: OrnamentKey) => {
      const el = createOrnament(stateRef.current.doc, key, defaults.stroke);
      add(el);
      return el;
    },
    [add, defaults],
  );

  const placeArtwork = React.useCallback(
    (natural: { width: number; height: number }) => {
      const r = placeArtworkInDoc(stateRef.current.doc, natural);
      commit(r.doc);
      setSelectedId(r.element.id);
      return r.element;
    },
    [commit],
  );

  const placeArtworkWithText = React.useCallback(
    (natural: { width: number; height: number }, texts: SeededWording) => {
      const r = placeArtworkInDoc(stateRef.current.doc, natural);
      const wording = [seededText(r.doc, "name", texts.name), seededText(r.doc, "line", texts.line)].filter(
        (el): el is TextElement => el !== null,
      );
      commit({ ...r.doc, elements: [...r.doc.elements, ...wording] });
      setSelectedId(null);
      return r.element;
    },
    [commit],
  );

  const selected = findElement(state.doc, selectedId);
  const style = React.useMemo(() => styleOf(selected, defaults), [selected, defaults]);

  return {
    doc: state.doc,
    set,
    update,
    add,
    remove,
    undo,
    canUndo: H.canUndo(state.history),
    clearAll,
    reset,
    selectedId,
    select: setSelectedId,
    selected,
    defaults,
    setDefaults,
    style,
    restyle,
    setBackground,
    setBottle,
    addText,
    addFrame,
    placeImage,
    applyTemplate,
    addOrnament,
    placeArtwork,
    placeArtworkWithText,
    isEmpty: state.doc.elements.length === 0,
    ornamentColor: defaults.stroke,
    canvasProps: {
      doc: state.doc,
      onChange: set,
      selectedId,
      onSelect: setSelectedId,
      defaults,
    },
  };
}


// ---------------------------------------------------------------------------
// LabelCanvas
// ---------------------------------------------------------------------------

/** Minimum CSS px between the (bleed-matted) label and the edge of the box at fit. */
const FIT_PAD = 20;
/** Print bleed shown around the label, mm. */
const BLEED_MM = 3;
/** Extra transparent gutter (CSS px) so handles on the label edge are never clipped. */
const HANDLE_GUTTER = 14;
/** Largest canvas backing store side, px (memory guard on zoomed hi-dpi screens). */
const MAX_BACKING_PX = 4096;
/** Two taps on the same text within this many ms open the editor. */
const DOUBLE_TAP_MS = 400;

type PointerKind = "mouse" | "pen" | "touch";

const grabPx = (t: PointerKind) => (t === "touch" ? 12 : t === "pen" ? 7 : 4);
const handlePx = (t: PointerKind) => (t === "touch" ? 20 : t === "pen" ? 12 : 9);
const dragThresholdPx = (t: PointerKind) => (t === "touch" ? 6 : 3);

interface View {
  zoom: number;
  x: number;
  y: number;
}

type Gesture =
  | {
      kind: "press";
      pointerId: number;
      pointerType: PointerKind;
      startClient: Point;
      startMm: Point;
      id: string;
      wasSelected: boolean;
      handle: Handle | null;
      startEl: LabelElement;
      key: string;
      active: boolean;
    }
  | {
      kind: "pan";
      pointerId: number;
      pointerType: PointerKind;
      startClient: Point;
      startView: View;
      canPan: boolean;
      moved: boolean;
    }
  | {
      kind: "place";
      pointerId: number;
      pointerType: PointerKind;
      startClient: Point;
      moved: boolean;
    }
  | { kind: "pinch"; startDist: number; startZoom: number; u0: Point };

interface Editing {
  id: string;
  key: string;
  created: boolean;
}

let gestureSeq = 0;
const nextKey = (prefix: string) => `${prefix}-${++gestureSeq}`;

function pointerKind(t: string): PointerKind {
  return t === "touch" || t === "pen" ? t : "mouse";
}

/** Re-measure a text element's height and keep it inside the label. */
function settleText(el: LabelElement, label: CanvasData["label"]): LabelElement {
  if (el.type !== "text") return el;
  const height = measureTextHeightMm(el);
  const y = Math.max(0, Math.min(el.y, label.height_mm - height));
  if (Math.abs(height - el.height) < 0.005 && y === el.y) return el;
  return { ...el, height, y: Math.round(y * 100) / 100 };
}

export function LabelCanvas({
  doc,
  onChange,
  assets,
  selectedId,
  onSelect,
  tool,
  onToolDone,
  zoom,
  onZoom,
  readOnly = false,
  onEditingChange,
  defaults = DEFAULT_STYLE,
  className,
  ariaLabel,
  ref,
}: LabelCanvasProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const stageRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const [box, setBox] = React.useState<{ w: number; h: number } | null>(null);
  const [view, setView] = React.useState<View>({ zoom: clampZoom(zoom), x: 0, y: 0 });
  const [editing, setEditing] = React.useState<Editing | null>(null);
  const [fontsTick, setFontsTick] = React.useState(0);
  const [ornamentsTick, setOrnamentsTick] = React.useState(0);
  const [announcement, setAnnouncement] = React.useState("");

  // Latest values for event handlers (avoid stale closures without re-binding).
  const docRef = React.useRef(doc);
  docRef.current = doc;
  const selectedRef = React.useRef(selectedId);
  selectedRef.current = selectedId;
  const viewRef = React.useRef(view);
  viewRef.current = view;
  const editingRef = React.useRef(editing);
  editingRef.current = editing;
  const propsRef = React.useRef({ onChange, onSelect, onZoom, onToolDone, tool, readOnly, defaults });
  propsRef.current = { onChange, onSelect, onZoom, onToolDone, tool, readOnly, defaults };

  const gestureRef = React.useRef<Gesture | null>(null);
  const pointersRef = React.useRef(new Map<number, { x: number; y: number; type: PointerKind }>());
  const lastTapRef = React.useRef<{ id: string; t: number } | null>(null);

  // ---- Layout: fit the label (plus its bleed mat) inside the box ------------

  const Wmm = doc.label.width_mm;
  const Hmm = doc.label.height_mm;
  let k = 0; // CSS px per mm at zoom 1
  if (box && box.w > 0 && box.h > 0) {
    k = Math.max(
      0.5,
      Math.min((box.w - 2 * FIT_PAD) / (Wmm + 2 * BLEED_MM), (box.h - 2 * FIT_PAD) / (Hmm + 2 * BLEED_MM)),
    );
  }
  const bleedPx = BLEED_MM * k;
  const G = bleedPx + HANDLE_GUTTER; // label origin inside the stage, CSS px
  const labelW = Wmm * k;
  const labelH = Hmm * k;
  const stageW = labelW + 2 * G;
  const stageH = labelH + 2 * G;
  const layoutRef = React.useRef({ k, G, stageW, stageH, box });
  layoutRef.current = { k, G, stageW, stageH, box };

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- View (zoom + pan) -------------------------------------------------------

  const clampView = React.useCallback((v: View): View => {
    const { stageW: sw, stageH: sh, box: b } = layoutRef.current;
    const z = clampZoom(v.zoom);
    if (!b || z <= 1) return { zoom: z, x: 0, y: 0 };
    const maxX = Math.max(0, (sw * z - b.w) / 2 + 24);
    const maxY = Math.max(0, (sh * z - b.h) / 2 + 24);
    return {
      zoom: z,
      x: Math.min(maxX, Math.max(-maxX, v.x)),
      y: Math.min(maxY, Math.max(-maxY, v.y)),
    };
  }, []);

  /** Apply a view; `notify` reports a zoom change to the parent. */
  const applyView = React.useCallback(
    (v: View, notify: boolean) => {
      const next = clampView(v);
      const prev = viewRef.current;
      viewRef.current = next;
      setView(next);
      if (notify && Math.abs(next.zoom - prev.zoom) > 1e-6) propsRef.current.onZoom(next.zoom);
    },
    [clampView],
  );

  /** Zoom to `z`, keeping the container point `anchor` (px from the box's top-left) still. */
  const zoomTo = React.useCallback(
    (z: number, anchor: Point | null, notify: boolean) => {
      const b = layoutRef.current.box;
      const cur = viewRef.current;
      const target = clampZoom(z);
      const c = b ? { x: b.w / 2, y: b.h / 2 } : { x: 0, y: 0 };
      const a = anchor ?? c;
      // Stage-local offset (from its centre) under the anchor, before and after.
      const u = { x: (a.x - c.x - cur.x) / cur.zoom, y: (a.y - c.y - cur.y) / cur.zoom };
      applyView({ zoom: target, x: a.x - c.x - target * u.x, y: a.y - c.y - target * u.y }, notify);
    },
    [applyView],
  );

  // Parent-driven zoom (toolbar buttons): anchor at the centre, do not echo back.
  React.useEffect(() => {
    if (Math.abs(clampZoom(zoom) - viewRef.current.zoom) > 1e-6) zoomTo(zoom, null, false);
  }, [zoom, zoomTo]);

  // Re-clamp pan when the box or label changes size.
  React.useEffect(() => {
    applyView(viewRef.current, false);
  }, [box, Wmm, Hmm, applyView]);

  // ---- Fonts --------------------------------------------------------------------

  const faceKey = doc.elements
    .map((e) => (e.type === "text" ? `${e.font}/${e.weight}/${e.italic}` : ""))
    .join("|");
  React.useEffect(() => {
    let alive = true;
    loadFonts(docRef.current).then(() => alive && setFontsTick((t) => t + 1));
    return () => {
      alive = false;
    };
  }, [faceKey]);
  React.useEffect(() => {
    if (typeof document === "undefined" || !("fonts" in document)) return;
    const onDone = () => setFontsTick((t) => t + 1);
    document.fonts.addEventListener("loadingdone", onDone);
    return () => document.fonts.removeEventListener("loadingdone", onDone);
  }, []);

  // Ornaments decode asynchronously (a dashed box shows meanwhile): redraw as each lands.
  React.useEffect(() => subscribeOrnaments(() => setOrnamentsTick((t) => t + 1)), []);

  // Once real fonts are measurable, re-measure every text height (no history).
  React.useEffect(() => {
    if (fontsTick === 0) return;
    const cur = docRef.current;
    let changed = false;
    const elements = cur.elements.map((el) => {
      const next = settleText(el, cur.label);
      if (next !== el) changed = true;
      return next;
    });
    if (changed) propsRef.current.onChange({ ...cur, elements }, { history: "skip" });
  }, [fontsTick]);

  // ---- Coordinates ----------------------------------------------------------------

  const clientToMm = React.useCallback((clientX: number, clientY: number): Point | null => {
    const stage = stageRef.current;
    const { k: kk, G: gg, stageW: sw } = layoutRef.current;
    if (!stage || kk <= 0) return null;
    const r = stage.getBoundingClientRect();
    if (r.width === 0) return null;
    const s = r.width / sw; // on-screen scale of the stage (= zoom)
    return {
      x: ((clientX - r.left) / s - gg) / kk,
      y: ((clientY - r.top) / s - gg) / kk,
    };
  }, []);

  const clientToBox = React.useCallback((clientX: number, clientY: number): Point => {
    const r = containerRef.current?.getBoundingClientRect();
    return r ? { x: clientX - r.left, y: clientY - r.top } : { x: clientX, y: clientY };
  }, []);

  /** Screen px → mm at the current zoom. */
  const screenToMm = React.useCallback((px: number) => {
    const kk = layoutRef.current.k * viewRef.current.zoom;
    return kk > 0 ? px / kk : 0;
  }, []);

  // ---- Editing ----------------------------------------------------------------------

  const stopEditing = React.useCallback(() => {
    const ed = editingRef.current;
    if (!ed) return;
    editingRef.current = null;
    setEditing(null);
    const cur = docRef.current;
    const el = findElement(cur, ed.id);
    if (el && el.type === "text" && el.text.trim() === "") {
      // An empty text element is removed. If it was created by this edit, its
      // creation record is dropped too, so Undo does not step through a no-op.
      if (ed.created) propsRef.current.onChange(removeElement(cur, ed.id), { history: "drop", key: ed.key });
      else propsRef.current.onChange(removeElement(cur, ed.id));
      propsRef.current.onSelect(null);
    }
    const ta = textareaRef.current;
    if (ta && typeof document !== "undefined" && document.activeElement === ta) {
      containerRef.current?.focus({ preventScroll: true });
    }
  }, []);

  const beginEditing = React.useCallback(
    (id: string, key: string, created: boolean) => {
      if (propsRef.current.readOnly) return;
      if (editingRef.current && editingRef.current.id !== id) stopEditing();
      const ed = { id, key, created };
      editingRef.current = ed;
      setEditing(ed);
      propsRef.current.onSelect(id);
      // Synchronous focus inside the user gesture — iOS only raises the
      // keyboard for a focus() made while handling the tap.
      const ta = textareaRef.current;
      if (ta) {
        ta.focus({ preventScroll: true });
        const len = findElement(docRef.current, id)?.type === "text"
          ? (findElement(docRef.current, id) as TextElement).text.length
          : 0;
        try {
          ta.setSelectionRange(len, len);
        } catch {
          /* not focusable yet — harmless */
        }
      }
    },
    [stopEditing],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      startEditing: (id: string) => {
        // A text element that is not in the doc yet (added in this same click
        // handler) or is still empty counts as newly created: typing folds into
        // its creation undo entry and closing it empty removes it cleanly.
        const el = findElement(docRef.current, id);
        const created = !el || (el.type === "text" && el.text === "");
        beginEditing(id, created ? `create-${id}` : nextKey(`edit-${id}`), created);
      },
      stopEditing,
    }),
    [beginEditing, stopEditing],
  );

  React.useEffect(() => {
    onEditingChange?.(editing !== null);
    // Only when editing starts/stops.
  }, [editing !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the edited element disappears (undo, delete elsewhere), close the editor.
  React.useEffect(() => {
    if (editing && !findElement(doc, editing.id)) {
      editingRef.current = null;
      setEditing(null);
    }
  }, [doc, editing]);

  const onTextInput = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const ed = editingRef.current;
    if (!ed) return;
    const cur = docRef.current;
    const el = findElement(cur, ed.id);
    if (!el || el.type !== "text") return;
    const next = settleText({ ...el, text: e.target.value.slice(0, 400) }, cur.label);
    propsRef.current.onChange(replaceElement(cur, next), { key: ed.key });
  }, []);

  const onTextKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      if (e.key === "Escape" || (e.key === "Enter" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        stopEditing();
      }
    },
    [stopEditing],
  );

  // ---- Wheel / Safari gesture events (native, non-passive) ------------------------

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        // ctrl+wheel is also what a trackpad pinch reports.
        e.preventDefault();
        const d = Math.max(-40, Math.min(40, e.deltaY));
        zoomTo(viewRef.current.zoom * Math.exp(-d * 0.006), clientToBox(e.clientX, e.clientY), true);
      } else if (viewRef.current.zoom > 1) {
        e.preventDefault();
        const v = viewRef.current;
        applyView({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }, false);
      }
    };
    const swallow = (e: Event) => e.preventDefault();
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("gesturestart", swallow);
    el.addEventListener("gesturechange", swallow);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", swallow);
      el.removeEventListener("gesturechange", swallow);
    };
  }, [zoomTo, applyView, clientToBox]);

  // ---- Pointer gestures -------------------------------------------------------------

  const startPinch = React.useCallback(() => {
    const touches = [...pointersRef.current.values()].filter((p) => p.type === "touch");
    if (touches.length < 2) return;
    const [a, b] = touches;
    const box = layoutRef.current.box;
    const mid = clientToBox((a.x + b.x) / 2, (a.y + b.y) / 2);
    const c = box ? { x: box.w / 2, y: box.h / 2 } : { x: 0, y: 0 };
    const v = viewRef.current;
    gestureRef.current = {
      kind: "pinch",
      startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      startZoom: v.zoom,
      u0: { x: (mid.x - c.x - v.x) / v.zoom, y: (mid.y - c.y - v.y) / v.zoom },
    };
  }, [clientToBox]);

  const setCursor = (c: string) => {
    const el = containerRef.current;
    if (el && el.style.cursor !== c) el.style.cursor = c;
  };

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.target === textareaRef.current) return;
      const type = pointerKind(e.pointerType);
      if (type === "mouse" && e.button !== 0) return;
      if (editingRef.current) stopEditing();
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, type });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      // A second finger turns whatever was happening into a pinch.
      const touches = [...pointersRef.current.values()].filter((p) => p.type === "touch");
      if (touches.length >= 2) {
        startPinch();
        return;
      }
      if (pointersRef.current.size > 1) return;

      const p = clientToMm(e.clientX, e.clientY);
      if (!p) return;
      const { tool: t, readOnly: ro } = propsRef.current;
      const startClient = { x: e.clientX, y: e.clientY };

      if (!ro && t === "text") {
        gestureRef.current = { kind: "place", pointerId: e.pointerId, pointerType: type, startClient, moved: false };
        return;
      }

      if (!ro) {
        const cur = docRef.current;
        const sel = findElement(cur, selectedRef.current);
        const handle = sel ? hitHandle(sel, p, screenToMm(handlePx(type))) : null;
        const hit = sel && handle ? sel : hitTest(cur, p, screenToMm(grabPx(type)), selectedRef.current);
        if (hit) {
          const wasSelected = hit.id === selectedRef.current;
          if (!wasSelected) propsRef.current.onSelect(hit.id);
          gestureRef.current = {
            kind: "press",
            pointerId: e.pointerId,
            pointerType: type,
            startClient,
            startMm: p,
            id: hit.id,
            wasSelected,
            handle,
            startEl: hit,
            key: nextKey(handle ? `resize-${hit.id}` : `move-${hit.id}`),
            active: false,
          };
          return;
        }
      }

      gestureRef.current = {
        kind: "pan",
        pointerId: e.pointerId,
        pointerType: type,
        startClient,
        startView: viewRef.current,
        canPan: viewRef.current.zoom > 1.001,
        moved: false,
      };
    },
    [clientToMm, screenToMm, startPinch, stopEditing],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const type = pointerKind(e.pointerType);
      if (pointersRef.current.has(e.pointerId)) {
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, type });
      }
      const g = gestureRef.current;

      // Hover feedback (mouse/pen without a gesture).
      if (!g && type !== "touch") {
        const { tool: t, readOnly: ro } = propsRef.current;
        if (ro) return setCursor(viewRef.current.zoom > 1 ? "grab" : "default");
        if (t === "text") return setCursor("crosshair");
        const p = clientToMm(e.clientX, e.clientY);
        if (!p) return;
        const cur = docRef.current;
        const sel = findElement(cur, selectedRef.current);
        const h = sel ? hitHandle(sel, p, screenToMm(handlePx(type))) : null;
        if (h) return setCursor(handleCursor(h));
        const hit = hitTest(cur, p, screenToMm(grabPx(type)), selectedRef.current);
        return setCursor(hit ? "move" : viewRef.current.zoom > 1 ? "grab" : "default");
      }
      if (!g) return;

      if (g.kind === "pinch") {
        const touches = [...pointersRef.current.values()].filter((p) => p.type === "touch");
        if (touches.length < 2) return;
        const [a, b] = touches;
        const box = layoutRef.current.box;
        const mid = clientToBox((a.x + b.x) / 2, (a.y + b.y) / 2);
        const c = box ? { x: box.w / 2, y: box.h / 2 } : { x: 0, y: 0 };
        const z = clampZoom((g.startZoom * Math.hypot(a.x - b.x, a.y - b.y)) / g.startDist);
        applyView({ zoom: z, x: mid.x - c.x - z * g.u0.x, y: mid.y - c.y - z * g.u0.y }, true);
        return;
      }
      if (e.pointerId !== g.pointerId) return;
      const dist = Math.hypot(e.clientX - g.startClient.x, e.clientY - g.startClient.y);

      if (g.kind === "place") {
        if (dist > dragThresholdPx(g.pointerType)) g.moved = true;
        return;
      }
      if (g.kind === "pan") {
        if (!g.moved && dist > dragThresholdPx(g.pointerType)) g.moved = true;
        if (g.moved && g.canPan) {
          setCursor("grabbing");
          applyView(
            {
              ...g.startView,
              x: g.startView.x + (e.clientX - g.startClient.x),
              y: g.startView.y + (e.clientY - g.startClient.y),
            },
            false,
          );
        }
        return;
      }
      // press → move / resize
      if (!g.active) {
        if (dist <= dragThresholdPx(g.pointerType)) return;
        g.active = true;
      }
      const p = clientToMm(e.clientX, e.clientY);
      if (!p) return;
      const cur = docRef.current;
      const dx = p.x - g.startMm.x;
      const dy = p.y - g.startMm.y;
      let next: LabelElement = g.handle
        ? resizeElement(g.startEl, g.handle, dx, dy, cur.label, { keepAspect: e.shiftKey })
        : moveElement(g.startEl, dx, dy, cur.label);
      next = settleText(next, cur.label);
      if (!findElement(cur, g.id)) return; // removed mid-gesture
      propsRef.current.onChange(replaceElement(cur, next), { key: g.key });
    },
    [applyView, clientToBox, clientToMm, screenToMm],
  );

  const endPointer = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>, cancelled: boolean) => {
      pointersRef.current.delete(e.pointerId);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const g = gestureRef.current;
      if (!g) return;
      if (g.kind === "pinch") {
        // Lifting one finger of a pinch must not turn the other into a drag.
        const touches = [...pointersRef.current.values()].filter((p) => p.type === "touch");
        if (touches.length >= 2) startPinch();
        else gestureRef.current = null;
        return;
      }
      if (e.pointerId !== g.pointerId) return;
      gestureRef.current = null;
      if (cancelled) return;
      const props = propsRef.current;

      if (g.kind === "place") {
        if (g.moved) return;
        const p = clientToMm(e.clientX, e.clientY);
        const cur = docRef.current;
        if (!p || p.x < 0 || p.y < 0 || p.x > cur.label.width_mm || p.y > cur.label.height_mm) return;
        const el = createText(cur, p, props.defaults);
        const key = `create-${el.id}`;
        props.onChange({ ...cur, elements: [...cur.elements, settleText(el, cur.label)] }, { key });
        beginEditing(el.id, key, true);
        props.onToolDone?.();
        return;
      }
      if (g.kind === "pan") {
        if (!g.moved) props.onSelect(null);
        setCursor(viewRef.current.zoom > 1 ? "grab" : "default");
        return;
      }
      // press
      if (g.active) return;
      const el = findElement(docRef.current, g.id);
      if (!el || el.type !== "text" || props.readOnly) {
        lastTapRef.current = { id: g.id, t: e.timeStamp };
        return;
      }
      const last = lastTapRef.current;
      const doubleTap = last && last.id === g.id && e.timeStamp - last.t < DOUBLE_TAP_MS;
      lastTapRef.current = { id: g.id, t: e.timeStamp };
      // Tapping text that was already selected (or a double-tap) opens the editor.
      if (g.wasSelected || doubleTap) beginEditing(g.id, nextKey(`edit-${g.id}`), false);
    },
    [beginEditing, clientToMm, startPinch],
  );

  const onPointerUp = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => endPointer(e, false), [endPointer]);
  const onPointerCancel = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => endPointer(e, true), [endPointer]);

  // ---- Keyboard ------------------------------------------------------------------

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.target === textareaRef.current || editingRef.current) return;
      const props = propsRef.current;
      const cur = docRef.current;
      const sel = findElement(cur, selectedRef.current);

      if (e.key === "Escape") {
        if (sel) {
          e.preventDefault();
          props.onSelect(null);
        }
        return;
      }
      if (e.key === "]" || e.key === "[") {
        // Keyboard selection: step through elements in stacking order.
        const n = cur.elements.length;
        if (n === 0) return;
        e.preventDefault();
        const i = sel ? cur.elements.findIndex((x) => x.id === sel.id) : -1;
        const j = e.key === "]" ? (i + 1 + n) % n : (i - 1 + n) % n;
        props.onSelect(cur.elements[i === -1 && e.key === "[" ? n - 1 : j].id);
        return;
      }
      if (!sel || props.readOnly) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        props.onChange(removeElement(cur, sel.id));
        props.onSelect(null);
        setAnnouncement(`${describeElement(sel)} deleted`);
        return;
      }
      if (e.key === "Enter" && sel.type === "text") {
        e.preventDefault();
        beginEditing(sel.id, nextKey(`edit-${sel.id}`), false);
        return;
      }
      const dir =
        e.key === "ArrowLeft" ? "left" : e.key === "ArrowRight" ? "right" : e.key === "ArrowUp" ? "up" : e.key === "ArrowDown" ? "down" : null;
      if (dir) {
        e.preventDefault();
        const next = nudge(sel, dir, e.shiftKey ? 5 : 1, cur.label);
        if (next !== sel && (next.x !== sel.x || next.y !== sel.y)) {
          props.onChange(replaceElement(cur, next), { key: `nudge-${sel.id}` });
        }
      }
    },
    [beginEditing],
  );

  // Announce selection changes to screen readers.
  React.useEffect(() => {
    const el = findElement(docRef.current, selectedId);
    if (el) setAnnouncement(`${describeElement(el)} selected`);
  }, [selectedId]);

  // ---- Draw --------------------------------------------------------------------------

  const editingId = editing?.id ?? null;
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || k <= 0) return;
    const frame = requestAnimationFrame(() => {
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      // Sharper backing store while zoomed (the CSS transform would otherwise upscale a bitmap).
      let bs = dpr * Math.min(Math.max(1, view.zoom), 3);
      bs = Math.min(bs, MAX_BACKING_PX / Math.max(stageW, stageH));
      const bw = Math.max(1, Math.round(stageW * bs));
      const bh = Math.max(1, Math.round(stageH * bs));
      if (canvas.width !== bw) canvas.width = bw;
      if (canvas.height !== bh) canvas.height = bh;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bw, bh);
      ctx.setTransform(bw / stageW, 0, 0, bh / stageH, 0, 0);
      ctx.translate(G, G);
      drawLabel(ctx, doc, k, assets, { skipIds: editingId ? [editingId] : undefined, placeholders: true });

      // Selection chrome — constant on-screen size regardless of zoom.
      const sel = !readOnly ? findElement(doc, selectedId) : null;
      if (sel && sel.id !== editingId) {
        const z = view.zoom;
        const lw = 1.25 / z;
        const pad = sel.type === "line" ? 3 / z : 0;
        const x = sel.x * k;
        const y = sel.y * k - pad;
        const w = sel.width * k;
        const h = sel.height * k + 2 * pad;
        ctx.save();
        ctx.lineWidth = lw * 3;
        ctx.strokeStyle = "rgba(8,7,6,0.45)";
        ctx.strokeRect(x, y, w, h);
        ctx.lineWidth = lw;
        ctx.strokeStyle = PALETTE_HEX.gold;
        ctx.strokeRect(x, y, w, h);
        const hs = 8 / z;
        for (const { point } of handlePoints(sel)) {
          const hx = point.x * k - hs / 2;
          const hy = point.y * k - hs / 2;
          ctx.fillStyle = PALETTE_HEX["warm-white"];
          ctx.fillRect(hx, hy, hs, hs);
          ctx.lineWidth = lw;
          ctx.strokeStyle = PALETTE_HEX.charcoal;
          ctx.strokeRect(hx, hy, hs, hs);
        }
        ctx.restore();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [doc, assets, selectedId, editingId, readOnly, k, G, stageW, stageH, view.zoom, fontsTick, ornamentsTick]);

  // ---- Render ----------------------------------------------------------------------------

  const editingEl = editing ? findElement(doc, editing.id) : null;
  const editStyle: React.CSSProperties | null =
    editingEl && editingEl.type === "text" && k > 0
      ? (() => {
          const face = resolveFace(editingEl.font, editingEl.weight, editingEl.italic);
          const fontPx = (editingEl.size_mm * k) / faceMetrics(face).capHeight;
          return {
            left: G + editingEl.x * k,
            top: G + editingEl.y * k,
            width: editingEl.width * k,
            height: Math.max(editingEl.height * k, fontPx * editingEl.line_height),
            fontFamily: cssFamily(editingEl.font),
            fontWeight: face.weight,
            fontStyle: face.italic ? "italic" : "normal",
            fontSize: `${fontPx}px`,
            lineHeight: editingEl.line_height,
            letterSpacing: `${editingEl.letter_spacing_em}em`,
            color: editingEl.color,
            caretColor: editingEl.color,
            textAlign: editingEl.align,
          };
        })()
      : null;

  const summary =
    ariaLabel ??
    `${doc.label.width_mm} by ${doc.label.height_mm} millimetre ${doc.bottle === "mcc" ? "MCC" : "wine"} label` +
      (doc.elements.length
        ? ` with ${doc.elements.map((el) => describeElement(el)).join(", ")}`
        : ", blank");

  const helpId = React.useId();

  return (
    <div
      ref={containerRef}
      role="group"
      aria-roledescription="label editor"
      aria-label="Label editor"
      aria-describedby={helpId}
      tabIndex={0}
      className={cn(
        "relative touch-none select-none overflow-hidden outline-none focus-visible:ring-1 focus-visible:ring-accent",
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
    >
      {k > 0 && box ? (
        <div
          ref={stageRef}
          className="absolute"
          style={{
            left: (box.w - stageW) / 2,
            top: (box.h - stageH) / 2,
            width: stageW,
            height: stageH,
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,
            transformOrigin: "50% 50%",
          }}
        >
          {/* The paper: a surface mat showing the 3 mm print bleed, with a hairline and a soft shadow. */}
          <div
            aria-hidden="true"
            className="absolute border border-hairline bg-surface"
            style={{
              left: G - bleedPx,
              top: G - bleedPx,
              width: labelW + 2 * bleedPx,
              height: labelH + 2 * bleedPx,
              boxShadow: "0 22px 48px -22px rgba(0,0,0,0.6), 0 3px 10px -4px rgba(0,0,0,0.3)",
            }}
          />
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={summary}
            className="absolute left-0 top-0 block"
            style={{ width: stageW, height: stageH }}
          />
          <textarea
            ref={textareaRef}
            aria-label="Label text"
            tabIndex={editStyle ? 0 : -1}
            value={editingEl && editingEl.type === "text" ? editingEl.text : ""}
            onChange={onTextInput}
            onKeyDown={onTextKeyDown}
            onBlur={stopEditing}
            spellCheck={false}
            autoCapitalize="sentences"
            className={cn(
              "absolute m-0 resize-none overflow-hidden border-0 bg-transparent p-0 shadow-none",
              editStyle
                ? "outline-1 outline-offset-2 outline-dashed outline-accent"
                : "pointer-events-none left-0 top-0 h-px w-px opacity-0",
            )}
            style={{
              ...(editStyle ?? {}),
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          />
        </div>
      ) : (
        // SSR / first paint: keep the textarea mounted so focus works immediately after hydration.
        <textarea ref={textareaRef} aria-label="Label text" tabIndex={-1} readOnly value="" className="pointer-events-none absolute left-0 top-0 h-px w-px opacity-0" />
      )}
      <span id={helpId} className="sr-only">
        {readOnly
          ? "Pinch or use Control and the mouse wheel to zoom."
          : "Tap an element to select it; drag to move; drag a corner handle to resize. Arrow keys nudge by 1 millimetre, with Shift by 5. Square brackets step through elements. Enter edits text, Delete removes the selection, Escape deselects."}
      </span>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
    </div>
  );
}

export default LabelCanvas;
